import React, { useMemo, useState } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Svg,
  G,
  Path,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

import MyriadProLight from "./pdfFonts/MyriadPro-Light.woff";
import MyriadProRegular from "./pdfFonts/MYRIADPRO-REGULAR.woff";
import MyriadProLightItalic from "./pdfFonts/Myriad Pro Light Italic.otf";
import MyriadProRegularItalic from "./pdfFonts/Myriad Pro Italic.ttf";
import moment from "moment";
import {
  convertTimeZoneDisplayText,
  formatTimeFromDb,
} from "ManagementTeam/utils/timezone";

Font.register({
  family: "Myriad Pro",
  fonts: [
    {
      src: MyriadProRegular,
      fontWeight: "bold",
    },
    {
      src: MyriadProLight,
      fontWeight: "light",
    },
    {
      src: MyriadProRegularItalic,
      fontWeight: "bold",
      fontStyle: "italic",
    },
    {
      src: MyriadProLightItalic,
      fontWeight: "light",
      fontStyle: "italic",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: "36px",
    fontFamily: "Myriad Pro",
    fontWeight: "light",
    fontSize: "12px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  date: {
    color: "#243746",
    fontSize: "18px",
    fontFamily: "Myriad Pro",
    fontWeight: "bold",
  },
  locationHeader: {
    color: "#243746",
    fontSize: "14px",
    fontWeight: "bold",
  },
  title: {
    color: "#415364",
    marginTop: "9px",
    fontSize: "24px",
  },
  subtitle: {
    color: "#243746",
    marginTop: "8px",
    fontSize: "12px",
  },
  attorneyNotice: {
    color: "#999999",
    fontWeight: "bold",
    fontSize: "14px",
  },
  bold: {
    fontFamily: "Myriad Pro",
    fontWeight: "bold",
  },
  light: {
    fontFamily: "Myriad Pro",
    fontWeight: "light",
  },
  agendaItem: {
    width: "100%",
    position: "relative",
    display: "flex",
    flexDirection: "row",
    minHeight: "24px",
    maxHeight: "100%",
    alignItems: "center",
    marginTop: "3px",
    marginBottom: "3px",
  },
  descriptionBox: {
    color: "#415364",
    display: "flex",
    flexDirection: "column",
    paddingLeft: "10px",
    whiteSpace: "pre-line",
    fontSize: "12px",
    width: "300px",
  },
  footer: {
    position: "absolute",
    fontSize: "8px",
    bottom: 0,
    width: "100%",
    display: "flex",
    maxHeight: "8px",
    textAlign: "start",
  },
  body: {
    width: "100%",
    flex: 1,
    position: "relative",
  },
  thumbContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "10px",
    textAlign: "center",
  },
  rightBox: {
    display: "flex",
    marginLeft: "120px",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  pdfIcon: {
    width: "20px",
  },
  attendanceContent: {
    fontSize: "12px",
  },
  diamondListStyle: {
    fontWeight: 400,
    fontSize: "10px",
    padding: "10px 10px",
    fontStyle: "italic",
  },
  listContainer: {
    minHeight: "180pt",
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    justifyContent: "flex-start",
    borderTop: "2pt solid #3f5364",
    marginBottom: "16pt",
  },
  listType: {
    border: "2pt solid #c7cfd2",
    borderRadius: "6pt",
    width: "10%",
    height: "25pt",
    marginTop: "16pt",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginRight: "8pt",
  },
  listColumn: {
    width: "45%",
    marginTop: "8pt",
  },
  listRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },
  listText: {
    width: "50%",
    display: "flex",
    flexDirection: "row",
  },
});

const todaysDate = dayjs().format("MMMM DD, YYYY");
const ProxyVoteIcon = () => (
  <Svg
    xmlns="http://www.w3.org/2000/svg"
    width="10"
    height="10"
    viewBox="0 0 25.947 30"
  >
    <G id="Proxy_Vote_Icon" transform="translate(2040.514 5194.245)">
      <Path
        id="Path_36400"
        data-name="Path 36400"
        d="M51.913,37.956A3.956,3.956,0,1,1,47.956,34a3.94,3.94,0,0,1,3.956,3.956"
        transform="translate(-2074.397 -5221.104)"
        fill="#243746"
      />
      <Path
        id="Path_36405"
        data-name="Path 36405"
        d="M48.346,53.774,44.874,48.1a4.926,4.926,0,0,0-4.351-2.593H33.137l-4.221-1.8L27.422,39.18a1.766,1.766,0,0,0-3.342,1.144L25.839,45.6a1.764,1.764,0,0,0,.967,1.054l4.221,1.758V59.094h10.99V57.336h0v-5.09h0V50.17l1.714,2.77H39.379a1.758,1.758,0,1,0,0,3.517h7.473a1.83,1.83,0,0,0,1.539-.879,1.766,1.766,0,0,0-.045-1.8Z"
        transform="translate(-2063.182 -5223.339)"
        fill="#243746"
      />
      <Path
        id="check"
        d="M8.056,12.818a.756.756,0,0,1-.612-.317L5.478,9.8a.819.819,0,0,1,.149-1.118A.746.746,0,0,1,6.7,8.833L8,10.611l3.253-5.438a.751.751,0,0,1,1.057-.255.818.818,0,0,1,.246,1.1L8.708,12.441a.76.76,0,0,1-.616.376H8.057Z"
        transform="translate(-2045.836 -5199.042)"
        fill="#243746"
      />
    </G>
  </Svg>
);
export function AttendancePdf({ attendanceItem }) {
  const [mt1List, setMT1List] = useState([]);
  const [mt2List, setMT2List] = useState([]);
  const [ecList, setECList] = useState([]);
  const [guestList, setGuestList] = useState([]);

  const {
    attendees,
    users,
    meetingDate,
    quorum,
    meetingLocation,
    meetingType,
    guestAttendees,
  } = attendanceItem;

  const checkChecked = (attendees, i) => {
    return attendees?.some((e) => (e._id === i._id ? true : false));
  };
  const checkCheckedProxy = (array, i) => {
    return array?.some((e) =>
      e._id === i._id && e.proxyCheckInBy ? true : false
    );
  };
  const handleSetDiamonds = (i, attendees, setList) => {
    setList((prev) => [
      ...prev,
      {
        userId: i._id,
        canSwap: i.profileB && i.profileB.firstName ? true : false,
        firstName: i.profile && i.profile.firstName ? i.profile.firstName : "",
        lastName: i.profile && i.profile.lastName ? i.profile.lastName : "",
        photo: i.profile && i.profile.photoUrl ? i.profile.photoUrl : "N/A",
        email: i.profile && i.profile.email ? i.profile.email : "N/A",
        phoneNumber: i.profile && i.profile.phone ? i.profile.phone : "N/A",
        address: i.profile && i.profile.address ? i.profile.address : "N/A",
        spouse: i.profile && i.profile.spouse ? i.profile.spouse : "",
        iboNum: i.iboNumber ? i.iboNumber : "N/A",
        birthdate:
          i.profile && i.profile.birthday
            ? moment.utc(i.profile.birthday).format("MMMM DD, YYYY")
            : "",
        anniversary:
          i.profile && i.profile.anniversary
            ? moment.utc(i.profile.anniversary).format("MMMM DD, YYYY")
            : "",
        uplineDiamond:
          i.businessInfo && i.businessInfo.uplineDiamond
            ? i.businessInfo.uplineDiamond
            : "",
        yearDiamondQualified:
          i.businessInfo && i.businessInfo.yearDiamondQualified
            ? i.businessInfo.yearDiamondQualified
            : "",
        yearQualified: i.businessInfo
          ? i.businessInfo.yearDiamondQualified
          : "",
        gracePeriod:
          i.businessInfo && i.businessInfo.gracePeriod
            ? i.businessInfo.gracePeriod.isGracePeriod
            : "",

        gracePeriodStarted:
          i.businessInfo &&
          i.businessInfo.gracePeriod &&
          i.businessInfo.gracePeriod.startedOn
            ? moment
                .utc(i.businessInfo.gracePeriod.startedOn)
                .format("MMMM DD, YYYY")
            : "",
        familyReunion: i.eventInfo ? i.eventInfo.hasFamilyReunion : "",
        firstFamilyReunion:
          i.eventInfo && i.eventInfo.firstFamilyReunion
            ? i.eventInfo.firstFamilyReunion
            : "",
        familyReunionAttendance:
          i.eventInfo && i.eventInfo.attendanceAtLastFamilyReunion
            ? i.eventInfo.attendanceAtLastFamilyReunion
            : "",
        votingMemberYear: i.businessInfo
          ? i.businessInfo.yearVotingMemberQualified
          : "",
        pinLevel:
          i.businessInfo &&
          i.businessInfo.qualifyingRequirementsByYear[0] &&
          i.businessInfo.qualifyingRequirementsByYear[0].pinWearing
            ? i.businessInfo.qualifyingRequirementsByYear[0].pinWearing
            : "-",
        garPoints:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0].garPoints
            : 0,
        integratedGarPoints:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0].integratedGarPoints
            : 0,
        diamondRankGar:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0].diamondRankGar
            : 0,
        diamondRankWwg:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0].diamondRankWWG
            : 0,
        capitalShare:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0].capitalShare
            : 0,
        accessLevel: i.accessLevel.includes("EC")
          ? "EC"
          : i.accessLevel.includes("MT1")
          ? "MT1"
          : i.accessLevel.includes("MT2")
          ? "MT2"
          : i.accessLevel.includes("GUEST")
          ? "GUEST"
          : "NONE",
        checkedIn: checkChecked(attendees, i),
        proxyCheckedIn: checkCheckedProxy(attendees, i),
        votingMember:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0].isVotingMember
            : false,
        executiveCommittee:
          i.businessInfo && i.businessInfo.qualifyingRequirementsByYear[0]
            ? i.businessInfo.qualifyingRequirementsByYear[0]
                .isExecutiveCommittee
            : false,
        yearExecutiveCommitteeQualified: i.businessInfo
          ? i.businessInfo.yearExecutiveCommitteeQualified
          : "",

        iboB: i.profileB ? i.profileB : null,

        //TODO: committees for profileB when IBO B Card is ready
        committees: i.profile.committees,
        committeesB:
          i.profileB && i.profileB.committees ? i.profileB.committees : [],
      },
    ]);
  };
  const meetingControls = useMemo(() => {
    setMT1List([]);
    setMT2List([]);
    setECList([]);
    setGuestList([]);
    if (users !== undefined) {
      var checkCount = 0;
      users
        .filter(
          (user) =>
            user.profile.firstName !== "Attorneys" &&
            user.profile.firstName !== "WWG" &&
            user.profile.firstName !== "WWG Guest"
        )
        .map((i, index) => {
          if (i.isDiamond === true && i.accessLevel.includes("EC")) {
            handleSetDiamonds(i, attendees, setECList);
            attendees?.map((e) => e._id === i._id && checkCount++);
          }
          if (i.isDiamond === true && i.accessLevel.includes("MT1")) {
            handleSetDiamonds(i, attendees, setMT1List);
            attendees?.map((e) => e._id === i._id && checkCount++);
          } else if (i.isDiamond === true && i.accessLevel.includes("MT2")) {
            handleSetDiamonds(i, attendees, setMT2List);
            attendees?.map((e) => e._id === i._id && checkCount++);
          }
          if (i.accessLevel.includes("GUEST")) {
            handleSetDiamonds(i, guestAttendees, setGuestList);
          }
          return index;
        });

      // meeting controls
      return {
        currentQuorum: checkCount,
        quorumTotal: quorum
          ? quorum
          : users
          ? Math.round(
              users.filter(
                (i) =>
                  i.isDiamond &&
                  i.accessLevel.length > 0 &&
                  (i.accessLevel.includes("MT1") ||
                    i.accessLevel.includes("MT2") ||
                    i.accessLevel.includes("EC")) &&
                  !(
                    i.accessLevel.includes("WWG") ||
                    i.accessLevel.includes("ATTORNEY") ||
                    i.accessLevel.includes("ADMIN")
                  )
              ).length * 0.7
            )
          : 0,
      };
    }

    return {};
  }, [users, quorum, attendees]);

  const getListAttendance = (list) => {
    return list.filter(
      (diamond) => diamond.checkedIn || diamond.proxyCheckedIn
    );
  };
  const getListAbsence = (list) => {
    return list.filter(
      (diamond) => !diamond.checkedIn && !diamond.proxyCheckedIn
    );
  };
  const renderListAttendee = (listAttendance, listAbsence, type, title) => {
    return (
      <View style={styles.listContainer}>
        <View
          style={[
            styles.listType,
            {
              backgroundColor:
                type === "MT1"
                  ? "#243746"
                  : type === "MT2"
                  ? "#91b6bb"
                  : type === "GUEST"
                  ? "white"
                  : type === "EC"
                  ? "#79531a"
                  : "",
              color: type === "GUEST" ? "#91b6bb" : "white",
            },
          ]}
        >
          <Text
            style={{
              fontSize: "14pt",
              marginTop: "4pt",
              fontFamily: "Myriad Pro",
              fontWeight: "bold",
            }}
          >
            {type}
          </Text>
        </View>
        <View style={styles.listColumn}>
          <Text style={[styles.subtitle, styles.bold, { marginBottom: "5pt" }]}>
            {title} in Attendance ({listAttendance.length})
          </Text>
          <View style={styles.listRow}>
            {listAttendance.map((e, index) => (
              <View key={index} style={styles.listText}>
                <Text>
                  {e.firstName} {e.lastName}{" "}
                </Text>
                {e.proxyCheckedIn && <ProxyVoteIcon />}
              </View>
            ))}
          </View>
        </View>
        {type !== "GUEST" && (
          <View style={styles.listColumn}>
            <Text
              style={[styles.subtitle, styles.bold, { marginBottom: "5pt" }]}
            >
              {title} Absent ({listAbsence.length})
            </Text>
            <View style={styles.listRow}>
              {listAbsence.map((e, index) => (
                <Text key={index} style={styles.listText}>
                  {e.firstName} {e.lastName}
                </Text>
              ))}
            </View>
          </View>
        )}
      </View>
    );
  };
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View
            style={[
              styles.row,
              {
                justifyContent: "flex-start",
              },
            ]}
          >
            <Text style={[styles.date]}>
              {new Date(meetingDate[0].startsOn).toDateString()}{" "}
            </Text>
            <Text style={[styles.attorneyNotice]}>
              | Confidential and Subject to Attorney Client Privilege
            </Text>
          </View>
          <Text style={[styles.locationHeader, styles.bold]}>
            {meetingLocation
              ? `${meetingLocation.location}${
                  meetingLocation.city ? `, ${meetingLocation.city}` : ""
                }${meetingLocation.state ? `, ${meetingLocation.state}` : ""}`
              : "Zoom"}
          </Text>
          <Text style={[styles.locationHeader, styles.bold]}>
            {meetingDate?.length > 0 &&
              `${formatTimeFromDb(
                new Date(meetingDate[0].startsOn).getUTCHours() +
                  ":" +
                  new Date(meetingDate[0].startsOn).getUTCMinutes(),
                meetingDate[0].timeZone
              )} `}
          </Text>
          <Text style={[styles.title, styles.bold]}>
            {meetingType === "MT"
              ? "Managment Team Attendance"
              : "Executive Committee Attendance"}
          </Text>
          <Text style={[styles.subtitle, styles.bold]}>
            Quorum Requirement: {meetingControls.quorumTotal}
          </Text>
        </View>

        {meetingType === "MT" && (
          <View style={[styles.body, { marginTop: "9px" }]}>
            {renderListAttendee(
              getListAttendance(mt1List),
              getListAbsence(mt1List),
              "MT1",
              "Voting Members"
            )}
            {renderListAttendee(
              getListAttendance(mt2List),
              getListAbsence(mt2List),
              "MT2",
              "Members"
            )}
            {renderListAttendee(
              getListAttendance(guestList),
              null,
              "GUEST",
              "Guests"
            )}
            <View style={styles.footer} fixed>
              <Text>
                {`This document is confidential and subject to attorney client privilege. For IBO Use Only. | Revised ${todaysDate} | CR# Not Reviewed`}
              </Text>
            </View>
          </View>
        )}

        {meetingType === "EC" && (
          <View style={[styles.body, { marginTop: "9px" }]}>
            {renderListAttendee(
              getListAttendance(ecList),
              getListAbsence(ecList),
              "EC",
              "EC Members"
            )}
            {renderListAttendee(guestList, null, "GUEST", "Guests")}
            <View style={styles.footer} fixed>
              <Text>
                {`This document is confidential and subject to attorney client privilege. For IBO Use Only. | Revised ${todaysDate} | CR# Not Reviewed`}
              </Text>
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
}
